<template>
    <validation-observer ref="observer" v-slot="{ handleSubmit }">
        <b-form id="rule-group-form"
                autocomplete="new-password"
                @submit.stop.prevent="handleSubmit(saveEditData)"
                @reset="resetForm"
        >
            <dl class="description-list-wrapper">
                <DescriptionItem :label="$t('규칙 그룹명')" mode="form" labelFor="edit-form-gorup-name"
                                 required
                >
                    <div class="w-100">
                        <validation-provider
                            v-slot="validationContext"
                            name="edit-form-gorup-name"
                            :rules="{ required: true, min: 2 }"
                        >
                            <b-form-input id="edit-form-gorup-name"
                                          v-model="form.name"
                                          name="edit-form-gorup-name"
                                          :disabled="editLoading"
                                          :placeholder="$t('그룹명')"
                                          :state="getValidationState(validationContext)"
                                          autocomplete="new-password"
                                          aria-describedby="edit-form-gorup-name-feedback"
                            />
                            <b-form-invalid-feedback id="edit-form-gorup-name-feedback">
                                {{ validationContext.errors[0] }}
                            </b-form-invalid-feedback>
                        </validation-provider>
                    </div>
                </DescriptionItem>
                
                <DescriptionItem :label="$t('연산자')" mode="form" labelFor="edit-form-operator" required>
                    <validation-provider
                        v-slot="validationContext"
                        name="edit-form-operator"
                        class="w-100"
                        :rules="{ required: true }"
                    >
                        <b-form-select id="edit-form-operator"
                                       v-model="form.operator"
                                       :disabled="editLoading"
                                       autocomplete="new-password"
                                       :state="getValidationState(validationContext)"
                                       aria-describedby="edit-form-operator-feedback"
                                       name="edit-form-operator"
                                       :options="operatorOptions"

                        />
                        <b-form-invalid-feedback id="edit-form-operator-feedback">
                            {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                    </validation-provider>
                </DescriptionItem>

                <DescriptionItem :label="$t('자식 그룹')" mode="form" labelFor="edit-form-value">
                    <div class="w-100 d-flex align-items-center justify-content-between">
                        <div class="badge-container">
                            <template v-if="selectedRuleGroups.length > 0">
                                <b-badge v-for="item in selectedRuleGroups" :key="item.id">
                                    {{ item.name}}
                                </b-badge>
                            </template>
                            <span v-else>선택 버튼을 클릭하여 자식 그룹을 선택해주세요.</span>
                        </div>

                        <div class="ml-2">
                            <b-button @click="openRuleGroupSearchModal">선택</b-button>
                        </div>
                    </div>
                </DescriptionItem>
                

                <DescriptionItem :label="$t('조건 선택')" mode="form" labelFor="edit-form-value">
                    <div class="w-100 d-flex align-items-center justify-content-between">
                       <div class="badge-container">
                           <template v-if="selectedConditions.length > 0">
                           <b-badge v-for="item in selectedConditions" :key="item.id">
                               {{ item.name}}
                           </b-badge>
                           </template>
                           <span v-else>선택 버튼을 클릭하여 조건을 선택해주세요.</span>
                       </div>
                        
                        <div class="ml-2">
                            <b-button @click="openConditionSearchModal">선택</b-button>
                        </div>
                    </div>
                </DescriptionItem>

                <DescriptionItem :label="$t('조건식')" mode="form" labelFor="edit-form-description">
                    <template v-if="getConditions">
                        <div v-for="(condition,idx) in getConditions" :key="`condition-${idx}`"
                             class="mr-1"
                        >
                            <b-badge class="f-90"
                                     variant="dark"
                            >
                                {{ condition }}
                            </b-badge>
                            <template v-if="idx !== getConditions.length - 1">
                                {{ form.operator }}
                            </template>
                        </div>
                    </template>
                    <template v-else>
                        -
                    </template>
                </DescriptionItem>

                <DescriptionItem :label="$t('설명')" mode="form" labelFor="edit-form-description">
                    <b-form-textarea id="edit-form-description"
                                     v-model="form.description"
                                     :disabled="editLoading"
                                     :placeholder="$t('설명 (선택사항)')"
                                     autocomplete="new-password"
                    />
                </DescriptionItem>

                <div v-if="mode === 'edit'" class="row-wrapper">
                    <DescriptionItem :label="$t('등록일시')">
                        {{ get_date(detailData.created_at) || '-' }}
                    </DescriptionItem>

                    <DescriptionItem :label="$t('수정일시')">
                        {{ get_date(detailData.updated_at) || '-' }}
                    </DescriptionItem>
                </div>
            </dl>
        </b-form>
        <ConditionSearchModal :selected-data="selectedConditionIds"
                              type="checkbox"
                              @on-select="onSelectCondition"
                              />
        <RuleGroupSearchModal :selected-data="selectedRuleGroupIds"
                              type="checkbox"
                              @on-select="onSelectRuleGroup"
        >
            
        </RuleGroupSearchModal>
    </validation-observer>
</template>

<script>
import {createRuleGroup, updateRuleGroup} from '@shared_utils/api/ruleEngine';
import {getErrorMessage} from '@core/utils/api/errorMessage';
import DescriptionItem from '@shared/components/common/DescriptionItem.vue';
import {operatorOptions} from '@shared/components/admin/pages/Management/CampaignConnectRule/RuleGroup/data';
import ConditionSearchModal
    from '@shared/components/admin/pages/Management/CampaignConnectRule/Condition/ConditionSearchModal.vue';
import RuleGroupSearchModal
    from '@shared/components/admin/pages/Management/CampaignConnectRule/RuleGroup/RuleGroupSearchModal.vue';
import {getDate} from '@shared/utils/common';

export default {
    name: 'RuleGroupForm',
    components: {
        RuleGroupSearchModal,
        ConditionSearchModal,
        DescriptionItem,
    },
    props: {
        detailData: {
            type: [Object, null],
            default: null,
        },
        parentData: {
            type: [Object, null],
            default: null,
        },
        mode: {
            type: String,
            required: true,
            default: 'add', // add | edit
        }
    },

    data() {
        return {
            editLoading: false,
            selectedConditions: [],
            selectedRuleGroups: [],
            form: {
                name: null,
                operator: null,
                description: null,
            },
            savedSnapshot: null,
        };
    },
    computed: {
        operatorOptions() {
            return operatorOptions;
        },
        // 부모 모달이 닫기 전 미저장 확인에 쓴다 (@dirty)
        dirty() {
            return this.savedSnapshot != null && this.snapshot() !== this.savedSnapshot;
        },
        selectedConditionIds() {
            return this.selectedConditions.map(item => item.id);
        },
        selectedRuleGroupIds(){
            return this.selectedRuleGroups.map(item => item.id);
        },
        getConditions(){
            if(this.form.operator == null){
                return null;
            }
            if(this.selectedConditions?.length < 1 && this.selectedRuleGroups?.length < 1){
                return null;
            }
            const conditions = this.selectedConditions.map(item => {
                return `${item.key} ${item.operator} ${item.value}`;
            });
            const childConditions = this.selectedRuleGroups.reduce((acc, cur) => {
                if(cur.conditions == null){
                    return acc;
                }
                if(cur.conditions.length > 1){
                    const conditions = cur.conditions?.map(item => {
                        return `(${item.key} ${item.operator} ${item.value})`;
                    });
                    acc.push(conditions.join(` ${cur.operator} ` ));
                }
                else {
                    const conditions = cur.conditions?.map(item => {
                        return `${item.key} ${item.operator} ${item.value}`;
                    });
                    acc.push(conditions.join(cur.operator));
                }
                return acc;
            }, []) ?? [];
            return [...conditions, ...childConditions];
        },
        get_date() {
            return (date) => {
                return getDate(date);
            };
        }
    },
    watch: {
        detailData: {
            immediate: true,
            handler() {
                this.form = {...this.form, ...this.detailData};
                if(this.detailData.conditions){
                    this.selectedConditions = this.detailData.conditions;
                }
                if(this.detailData.child_groups){
                    this.selectedRuleGroups = this.detailData.child_groups;
                }
                this.savedSnapshot = this.snapshot();
            }
        },
        dirty(value) {
            this.$emit('dirty', value);
        },
    },
    beforeDestroy() {
        this.$emit('dirty', false);
    },
    methods: {
        snapshot() {
            const {name, operator, description} = this.form;
            return JSON.stringify([name ?? '', operator ?? '', description ?? '', this.selectedConditionIds, this.selectedRuleGroupIds]);
        },
        getValidationState({dirty, validated, valid = null}) {
            return dirty || validated ? valid : null;
        },
        async saveEditData() {
            if (this.editLoading) return;
            if(this.selectedConditions?.length < 1 && this.selectedRuleGroups?.length < 1){
                this.$showModal({
                    type: 'alert',
                    state: 'warning',
                    title: '규칙 그룹 등록 실패',
                    message: '조건과 자식 그룹 중 하나는 반드시 선택해야합니다.'
                });
                return;
            }
            try {
                this.editLoading = true;
                
                let response = null;
                const form = {
                    ...this.form,
                    child_group_ids: this.selectedRuleGroupIds,
                    condition_ids: this.selectedConditionIds
                };
                
                if(this.mode === 'add'){
                    response =  await createRuleGroup(form);

                }
                else if (this.mode === 'edit'){
                    response =  await updateRuleGroup(this.detailData.id, form);
                }
               
                this.$EventBus.$emit('toast-success', '규칙 그룹 등록 및 수정', '규칙 그룹이 성공적으로 저장되었습니다.');
                this.$emit('updateDetailData', {...this.detailData, ...this.form, ...response.data});
            } catch (error) {
                console.error(error);
                const status = error?.response?.status;
                if (status === 401) return;
                // 입력 문제(4xx)는 서버 사유를 그대로 보여 관리자가 직접 고치게 한다
                const reason = status >= 400 && status < 500 ? getErrorMessage(error, '') : '';
                this.$EventBus.$emit('toast-failure', '규칙 그룹 등록 및 수정', reason || '규칙 그룹 데이터 저장 도중 오류가 발생했습니다. 개발 팀에 문의해주세요.');
            } finally {
                this.editLoading = false;
            }
        },
        resetForm() {
            this.form = {
                name: null,
                operator: null,
                description: null,
            };
            this.editLoading = false;
            this.selectedConditions = [];
        },
        openConditionSearchModal() {
            this.$bvModal.show('condition-search-modal');
        },
        openRuleGroupSearchModal() {
            this.$bvModal.show('rule-group-search-modal');
        },
        onSelectCondition(mapConditions){
            this.selectedConditions = Object.values(mapConditions);
        },
        onSelectRuleGroup(mapRuleGroup){
            this.selectedRuleGroups = Object.values(mapRuleGroup);
        }
    },
};
</script>
